import { useRef, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Search, Loader2, Sparkles, Wand2, Building2, Home, FlaskConical,
  ChevronDown, ChevronRight, Trash2, Plus, AlertTriangle, Paperclip, X, CornerDownRight,
  Columns2, UserSearch,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { usePageTitle } from "@/hooks/use-page-title";

type CompanyEntry = { orgId: number; email: string; name: string | null; company: string | null };
type PropertyEntry = {
  propertyId: number; name: string; address: string | null; city: string | null;
  state: string | null; orgId: number; ownerEmail: string; company: string | null;
};
type SourcesResp = { companies: CompanyEntry[]; properties: PropertyEntry[] };
type VoiceResp = { lockedContract: string; defaultVoice: string; effectiveVoice: string };

type RetrievedChunk = {
  chunkText: string;
  sourceType: string;
  propertyId: number | null;
  score: number;
  [k: string]: unknown;
};

function chunkSourceLabel(sourceType: string): string {
  switch (sourceType) {
    case "company_kb": return "Company KB";
    case "property_kb": return "Property KB";
    case "approved_memory": return "Approved memory";
    default: return sourceType || "unknown";
  }
}
type SuggestDebug = {
  queryText: string;
  retrievedChunks: RetrievedChunk[];
  contextBlock: string;
  pinnedVoice: RetrievedChunk[];
  pinnedVoiceBlock: string;
  houseVoice: string;
  imagesUsed: number;
  imageCaption: string | null;
  prompt: string;
  effectiveRunCount: number;
  lastHostIncluded: boolean;
  visionSummaries?: Array<{
    url: string;
    summary: { description: string; ocrText: string | null; apparentIssue: string | null } | null;
    cached: boolean;
  }>;
};
type ImproveDebug = {
  queryText: string;
  retrievedChunks: RetrievedChunk[];
  contextBlock: string;
  pinnedVoice: RetrievedChunk[];
  pinnedVoiceBlock: string;
  houseVoice: string;
  prompt: string;
};
// AI Draft Context Overhaul — exactly what the draft targeted and used.
type ContextBreakdown = {
  unresolvedMessageIds: number[];
  unresolvedTruncated: boolean;
  imagesAnalyzed: number;
  imagesCached: number;
  priorsConsidered: number;
  priorsKept: Array<{ messageId: number | null; reason: string }>;
  knowledgeChunkIds: number[];
  fallbackUsed: boolean;
  continuityCount: number;
};
type SuggestResp = {
  draft: string; hint?: string; chunksUsed: number; imagesUsed: number;
  context?: ContextBreakdown; debug: SuggestDebug;
};
type ImproveResp = { improved: string; chunksUsed: number; debug: ImproveDebug };
type CompareResp = { variants: SuggestResp[] };
type MemberEntry = {
  memberId: number; name: string | null; hostEmail: string | null;
  company: string | null; messageCount: number; unresolvedCount: number;
};
type MemberSuggestResp = SuggestResp & { memberName: string | null };

// A single output+debug payload, tagged so one panel component can render either.
type PanelData =
  | { kind: "suggest"; res: SuggestResp }
  | { kind: "improve"; res: ImproveResp };

type Attachment = { url: string; name: string };
// `resolved` (Phase 4): undefined = no marker. When ANY message in the
// conversation carries a marker, the server switches selection to the
// production unresolved-first semantics; with no markers, the historic
// unanswered-run heuristic applies.
type Msg = { id: number; role: "member" | "host"; text: string; attachments: Attachment[]; resolved?: boolean };
type VoiceMode = "live" | "override";

let msgSeq = 1;
const newMsg = (role: "member" | "host", text = "", attachments: Attachment[] = []): Msg => ({
  id: msgSeq++, role, text, attachments,
});

// Annotate an `/objects/<id>` upload path with the `?ct=<mime>` marker the core
// image fetcher reads (mirrors the production message-attachment contract).
function annotateObjectPath(objectPath: string, mime: string): string {
  const sep = objectPath.includes("?") ? "&" : "?";
  return mime ? `${objectPath}${sep}ct=${encodeURIComponent(mime)}` : objectPath;
}

function companyLabel(c: CompanyEntry): string {
  return c.company || c.name || c.email;
}
function propertyLabel(p: PropertyEntry): string {
  const loc = [p.city, p.state].filter(Boolean).join(", ");
  return loc ? `${p.name} — ${loc}` : p.name;
}

export default function AppDevToolsAiPlayground() {
  usePageTitle("Dev: AI Suggest Playground");
  const { toast } = useToast();

  // ── Source pickers ──
  const [search, setSearch] = useState("");
  const [companyOrgId, setCompanyOrgId] = useState<string>("");
  const [propertyId, setPropertyId] = useState<string>("__none__");

  // ── Voice ── (voiceMode/voiceOverride double as "Voice A" in compare mode)
  const [voiceMode, setVoiceMode] = useState<VoiceMode>("live");
  const [voiceOverride, setVoiceOverride] = useState("");
  // ── Voice B (compare mode only) ──
  const [compareMode, setCompareMode] = useState(false);
  const [voiceModeB, setVoiceModeB] = useState<VoiceMode>("override");
  const [voiceOverrideB, setVoiceOverrideB] = useState("");

  // ── Conversation builder ──
  const [messages, setMessages] = useState<Msg[]>([newMsg("member", "")]);
  const [draft, setDraft] = useState("");

  // ── Results ──
  const [suggestResult, setSuggestResult] = useState<SuggestResp | null>(null);
  const [improveResult, setImproveResult] = useState<ImproveResp | null>(null);
  const [compareResult, setCompareResult] = useState<SuggestResp[] | null>(null);
  const [showDebug, setShowDebug] = useState(true);

  // ── Real-member mode (Phase 4) ──
  const [memberSearch, setMemberSearch] = useState("");
  const [selectedMemberId, setSelectedMemberId] = useState<string>("");
  const [memberResult, setMemberResult] = useState<MemberSuggestResp | null>(null);

  const sourcesQuery = useQuery<SourcesResp>({
    queryKey: ["/api/app/ai-playground/sources", search],
    queryFn: async () => {
      const url = search
        ? `/api/app/ai-playground/sources?q=${encodeURIComponent(search)}`
        : "/api/app/ai-playground/sources";
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      return res.json();
    },
    staleTime: 30_000,
  });

  const voiceQuery = useQuery<VoiceResp>({
    queryKey: ["/api/app/ai-playground/voice"],
    queryFn: async () => {
      const res = await fetch("/api/app/ai-playground/voice", { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      return res.json();
    },
    staleTime: 60_000,
  });

  const membersQuery = useQuery<{ members: MemberEntry[] }>({
    queryKey: ["/api/app/ai-playground/members", memberSearch],
    queryFn: async () => {
      const url = memberSearch
        ? `/api/app/ai-playground/members?q=${encodeURIComponent(memberSearch)}`
        : "/api/app/ai-playground/members";
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      return res.json();
    },
    staleTime: 30_000,
  });

  const companies = sourcesQuery.data?.companies ?? [];
  const properties = sourcesQuery.data?.properties ?? [];
  const playgroundMembers = membersQuery.data?.members ?? [];
  const liveVoice = voiceQuery.data?.effectiveVoice || voiceQuery.data?.defaultVoice || "—";

  function parseErr(err: Error): string {
    const msg = err.message.replace(/^\d+:\s*/, "");
    try { return JSON.parse(msg)?.message ?? msg; } catch { return msg; }
  }

  // Voice mode is the explicit operator intent: "live" => use the current
  // effective global voice (send null), "override" => use the scratch text.
  function resolveVoicePayload(mode: VoiceMode, text: string): string | null {
    return mode === "override" && text.trim() ? text : null;
  }

  // Common scope + conversation payload shared by every action.
  function buildScope() {
    return {
      companyOrgId: Number(companyOrgId),
      propertyId: propertyId === "__none__" ? null : Number(propertyId),
      messages: messages
        .filter((m) => m.text.trim().length > 0 || m.attachments.length > 0)
        .map((m) => ({
          role: m.role,
          text: m.text,
          attachments: m.attachments.map((a) => a.url),
          ...(m.resolved !== undefined ? { resolved: m.resolved } : {}),
        })),
    };
  }

  function buildBody() {
    return { ...buildScope(), voiceOverride: resolveVoicePayload(voiceMode, voiceOverride) };
  }

  const suggestMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/ai-playground/suggest", buildBody());
      return (await res.json()) as SuggestResp;
    },
    onSuccess: (data) => {
      setSuggestResult(data);
      setImproveResult(null);
      setCompareResult(null);
      if (data.hint) toast({ title: "Heads up", description: data.hint });
    },
    onError: (err: Error) => toast({ title: "Suggest failed", description: parseErr(err), variant: "destructive" }),
  });

  const improveMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/ai-playground/improve", { ...buildBody(), draft });
      return (await res.json()) as ImproveResp;
    },
    onSuccess: (data) => {
      setImproveResult(data);
      setSuggestResult(null);
      setCompareResult(null);
    },
    onError: (err: Error) => toast({ title: "Improve failed", description: parseErr(err), variant: "destructive" }),
  });

  const compareMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/ai-playground/suggest-compare", {
        ...buildScope(),
        voices: [
          resolveVoicePayload(voiceMode, voiceOverride),
          resolveVoicePayload(voiceModeB, voiceOverrideB),
        ],
      });
      return (await res.json()) as CompareResp;
    },
    onSuccess: (data) => {
      setCompareResult(data.variants);
      setSuggestResult(null);
      setImproveResult(null);
      const hint = data.variants.find((v) => v.hint)?.hint;
      if (hint) toast({ title: "Heads up", description: hint });
    },
    onError: (err: Error) => toast({ title: "Compare failed", description: parseErr(err), variant: "destructive" }),
  });

  // Real-member mode: run the EXACT production selection path against a real
  // member's conversation (same storage queries + section-building code as
  // the member suggest route) and inspect the context breakdown. Read-only —
  // nothing is persisted, the draft is never sent.
  const memberSuggestMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/ai-playground/suggest-member", {
        memberId: Number(selectedMemberId),
      });
      return (await res.json()) as MemberSuggestResp;
    },
    onSuccess: (data) => {
      setMemberResult(data);
      if (data.hint) toast({ title: "Heads up", description: data.hint });
    },
    onError: (err: Error) => toast({ title: "Member suggest failed", description: parseErr(err), variant: "destructive" }),
  });

  // ── Conversation row helpers ──
  function updateMsg(id: number, patch: Partial<Msg>) {
    setMessages((arr) => arr.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  }
  // Upload a test image through the same presigned object-storage flow the
  // production composer uses, then attach the resulting `/objects/...` path so
  // the suggest pipeline fetches and "sees" the real bytes.
  async function uploadAttachment(id: number, file: File): Promise<void> {
    const res = await apiRequest("POST", "/api/app/messages/upload-url", {});
    const { uploadUrl, objectPath } = (await res.json()) as { uploadUrl: string; objectPath: string };
    const putRes = await fetch(uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": file.type || "application/octet-stream" },
      body: file,
    });
    if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);
    const url = annotateObjectPath(objectPath, file.type);
    setMessages((arr) => arr.map((x) =>
      x.id === id && x.attachments.length < 8
        ? { ...x, attachments: [...x.attachments, { url, name: file.name }] }
        : x));
  }
  function removeAttachment(id: number, idx: number) {
    setMessages((arr) => arr.map((x) =>
      x.id === id ? { ...x, attachments: x.attachments.filter((_, i) => i !== idx) } : x));
  }

  // Append a suggested draft into the conversation as a host message, then queue
  // a fresh member row so the operator can continue a multi-turn sim. Takes the
  // explicit draft text so a specific compare variant can be accepted.
  function acceptIntoThread(text: string | undefined | null) {
    const t = text?.trim();
    if (!t) return;
    setMessages((arr) => [...arr, newMsg("host", t), newMsg("member", "")]);
    setSuggestResult(null);
    setImproveResult(null);
    setCompareResult(null);
    toast({ title: "Accepted into thread", description: "AI reply added as a host message — add the next member message to continue." });
  }

  function toggleCompareMode() {
    setCompareMode((on) => {
      // Clear stale results whenever the run shape changes.
      setSuggestResult(null);
      setImproveResult(null);
      setCompareResult(null);
      return !on;
    });
  }

  const hasCompany = companyOrgId.trim().length > 0;
  const hasMember = messages.some((m) => m.role === "member" && (m.text.trim().length > 0 || m.attachments.length > 0));
  const canSuggest = hasCompany && hasMember && !suggestMutation.isPending;
  const canImprove = hasCompany && draft.trim().length > 0 && !improveMutation.isPending;
  const canCompare = hasCompany && hasMember && !compareMutation.isPending;

  return (
    <div className={`p-6 mx-auto space-y-8 ${compareMode ? "max-w-7xl" : "max-w-5xl"}`}>
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2" data-testid="heading-ai-playground">
          <FlaskConical className="h-6 w-6" />
          AI Suggest Playground
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Simulate a member conversation against any company KB and any property KB (they need not belong to the
          same org), with a chosen house voice, and inspect the full retrieval → prompt → generation pipeline.
        </p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="text-sm text-amber-800">
          <strong>Read-only.</strong> Nothing here is saved — no member timelines are written, the AI learning loop
          is never touched. The only effects are KB reads and a Gemini generation call (cost-logged to you).
        </div>
      </div>

      {/* ── Source selection ── */}
      <div className="border border-gray-200 rounded-lg p-5 space-y-4">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Knowledge sources</h2>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            data-testid="input-source-search"
            placeholder="Filter companies & properties…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        {sourcesQuery.isLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading sources…
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-1">
              <Building2 className="h-4 w-4" /> Company KB
            </label>
            <Select value={companyOrgId} onValueChange={setCompanyOrgId}>
              <SelectTrigger data-testid="select-company">
                <SelectValue placeholder="Select a company…" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((c) => (
                  <SelectItem key={c.orgId} value={String(c.orgId)} data-testid={`option-company-${c.orgId}`}>
                    {companyLabel(c)} <span className="text-gray-400">({c.email})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-1">
              <Home className="h-4 w-4" /> Property KB <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <Select value={propertyId} onValueChange={setPropertyId}>
              <SelectTrigger data-testid="select-property">
                <SelectValue placeholder="No property KB" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">No property KB</SelectItem>
                {properties.map((p) => (
                  <SelectItem key={p.propertyId} value={String(p.propertyId)} data-testid={`option-property-${p.propertyId}`}>
                    {propertyLabel(p)} <span className="text-gray-400">({p.company || p.ownerEmail})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <p className="text-xs text-gray-400">
          The company and property can come from different orgs — this is the only surface where KB scope is decoupled.
        </p>
      </div>

      {/* ── House voice ── */}
      <div className="border border-gray-200 rounded-lg p-5 space-y-3">
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">House voice</h2>
          <Button
            size="sm"
            variant={compareMode ? "default" : "outline"}
            onClick={toggleCompareMode}
            data-testid="button-toggle-compare"
          >
            <Columns2 className="h-3.5 w-3.5 mr-1.5" />
            {compareMode ? "Comparing two voices" : "Compare two voices"}
          </Button>
        </div>

        {compareMode ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <VoiceEditor
                label="Voice A"
                mode={voiceMode}
                onModeChange={setVoiceMode}
                override={voiceOverride}
                onOverrideChange={setVoiceOverride}
                liveVoice={liveVoice}
                liveLoading={voiceQuery.isLoading}
                idSuffix="-a"
              />
              <VoiceEditor
                label="Voice B"
                mode={voiceModeB}
                onModeChange={setVoiceModeB}
                override={voiceOverrideB}
                onOverrideChange={setVoiceOverrideB}
                liveVoice={liveVoice}
                liveLoading={voiceQuery.isLoading}
                idSuffix="-b"
              />
            </div>
            <p className="text-xs text-gray-400">
              Both variants run the same conversation + KB — only the house voice differs, so any divergence in the
              drafts or debug below is voice-driven. The saved global voice is never modified.
            </p>
          </>
        ) : (
          <>
            <VoiceEditor
              mode={voiceMode}
              onModeChange={setVoiceMode}
              override={voiceOverride}
              onOverrideChange={setVoiceOverride}
              liveVoice={liveVoice}
              liveLoading={voiceQuery.isLoading}
            />
            <p className="text-xs text-gray-400">
              {voiceMode === "live"
                ? "Simulation uses the current effective global house voice shown above."
                : "Simulation uses the scratch voice above — the saved global voice is never modified."}
            </p>
          </>
        )}
      </div>

      {/* ── Conversation ── */}
      <div className="border border-gray-200 rounded-lg p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Conversation</h2>
          <div className="flex gap-2">
            <Button
              size="sm" variant="outline"
              onClick={() => setMessages((m) => [...m, newMsg("member")])}
              data-testid="button-add-member-msg"
            >
              <Plus className="h-3.5 w-3.5 mr-1" /> Member
            </Button>
            <Button
              size="sm" variant="outline"
              onClick={() => setMessages((m) => [...m, newMsg("host")])}
              data-testid="button-add-host-msg"
            >
              <Plus className="h-3.5 w-3.5 mr-1" /> Host
            </Button>
          </div>
        </div>

        <div className="space-y-3">
          {messages.map((m, idx) => (
            <ConversationRow
              key={m.id}
              msg={m}
              idx={idx}
              canRemove={messages.length > 1}
              onRoleChange={(role) => updateMsg(m.id, { role })}
              onTextChange={(text) => updateMsg(m.id, { text })}
              onResolvedChange={(resolved) => updateMsg(m.id, { resolved })}
              onUploadAttachment={(file) => uploadAttachment(m.id, file)}
              onRemoveAttachment={(i) => removeAttachment(m.id, i)}
              onRemove={() => setMessages((arr) => (arr.length > 1 ? arr.filter((x) => x.id !== m.id) : arr))}
            />
          ))}
        </div>
        <p className="text-xs text-gray-400">
          Messages are oldest-first. Without resolution markers, Suggest replies to the latest unanswered member run
          since the last host message; mark any message Open/Resolved to switch to the production unresolved-first
          selection (with relevance-gated lookback). Attach image URLs to a member message to test image-aware replies.
        </p>
      </div>

      {/* ── Actions ── */}
      <div className="flex flex-wrap items-center gap-3">
        {compareMode ? (
          <Button
            onClick={() => compareMutation.mutate()}
            disabled={!canCompare}
            data-testid="button-compare"
          >
            {compareMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Columns2 className="h-4 w-4 mr-1.5" />}
            Compare voices
          </Button>
        ) : (
          <>
            <Button
              onClick={() => suggestMutation.mutate()}
              disabled={!canSuggest}
              data-testid="button-suggest"
            >
              {suggestMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
              Suggest with AI
            </Button>

            <div className="flex items-center gap-2 flex-1 min-w-[260px]">
              <Input
                data-testid="input-draft"
                placeholder="Draft to improve…"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
              />
              <Button
                variant="outline"
                onClick={() => improveMutation.mutate()}
                disabled={!canImprove}
                data-testid="button-improve"
              >
                {improveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Wand2 className="h-4 w-4 mr-1.5" />}
                Improve
              </Button>
            </div>
          </>
        )}
      </div>

      {/* ── Output (single mode) ── */}
      {!compareMode && suggestResult && (
        <ResultPanel
          data={{ kind: "suggest", res: suggestResult }}
          showDebug={showDebug}
          onToggleDebug={() => setShowDebug((s) => !s)}
          onAccept={acceptIntoThread}
        />
      )}
      {!compareMode && improveResult && (
        <ResultPanel
          data={{ kind: "improve", res: improveResult }}
          showDebug={showDebug}
          onToggleDebug={() => setShowDebug((s) => !s)}
        />
      )}

      {/* ── Output (compare mode) ── */}
      {compareMode && compareResult && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="compare-results">
          {compareResult.map((res, i) => (
            <ResultPanel
              key={i}
              title={i === 0 ? "Voice A" : "Voice B"}
              data={{ kind: "suggest", res }}
              showDebug={showDebug}
              onToggleDebug={() => setShowDebug((s) => !s)}
              onAccept={acceptIntoThread}
              idSuffix={i === 0 ? "-a" : "-b"}
            />
          ))}
        </div>
      )}

      {/* ── Real-member mode (Phase 4) ── */}
      <div className="border border-gray-200 rounded-lg p-5 space-y-4">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
          <UserSearch className="h-4 w-4" /> Real member — production selection
        </h2>
        <p className="text-xs text-gray-500">
          Runs the EXACT production selection path (the same storage queries and section-building code the member
          composer uses) against a real member's conversation, so route-level selection is verifiable here — the class
          of bug synthetic conversations can't reproduce. Read-only: nothing is written, the draft is never sent.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              data-testid="input-member-search"
              placeholder="Filter members (name, host email, company)…"
              value={memberSearch}
              onChange={(e) => setMemberSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={selectedMemberId} onValueChange={setSelectedMemberId}>
            <SelectTrigger data-testid="select-real-member">
              <SelectValue placeholder={membersQuery.isLoading ? "Loading members…" : "Select a member…"} />
            </SelectTrigger>
            <SelectContent>
              {playgroundMembers.map((m) => (
                <SelectItem key={m.memberId} value={String(m.memberId)} data-testid={`option-member-${m.memberId}`}>
                  {m.name || `Member #${m.memberId}`} — {m.unresolvedCount} open / {m.messageCount} msgs
                  {m.company || m.hostEmail ? ` (${m.company || m.hostEmail})` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          onClick={() => memberSuggestMutation.mutate()}
          disabled={!selectedMemberId || memberSuggestMutation.isPending}
          data-testid="button-suggest-member"
        >
          {memberSuggestMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
          Suggest for member
        </Button>

        {memberResult && (
          <ResultPanel
            title={memberResult.memberName ? `Real member: ${memberResult.memberName}` : "Real member"}
            data={{ kind: "suggest", res: memberResult }}
            showDebug={showDebug}
            onToggleDebug={() => setShowDebug((s) => !s)}
            idSuffix="-member"
          />
        )}
      </div>
    </div>
  );
}

// ── One result column: Output card + Pipeline debug card. Handles both the
// Suggest and Improve shapes and takes an id suffix so two panels can coexist
// with distinct test ids in compare mode. ──
function ResultPanel({
  title, data, showDebug, onToggleDebug, onAccept, idSuffix = "",
}: {
  title?: string;
  data: PanelData;
  showDebug: boolean;
  onToggleDebug: () => void;
  onAccept?: (draft: string) => void;
  idSuffix?: string;
}) {
  const suggest = data.kind === "suggest" ? data.res : null;
  const improve = data.kind === "improve" ? data.res : null;
  const debug: SuggestDebug | ImproveDebug = data.res.debug;

  const outputText = suggest
    ? (suggest.draft || suggest.hint || "—")
    : (improve?.improved ?? "—");
  const acceptableDraft = suggest?.draft.trim() ?? "";

  return (
    <div className="space-y-8">
      {/* Output */}
      <div className="border border-gray-200 rounded-lg p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
            {title ? `Output — ${title}` : "Output"}
          </h2>
          <div className="flex items-center gap-2">
            {suggest && (
              <>
                <Badge variant="secondary" data-testid={`badge-chunks-used${idSuffix}`}>{suggest.chunksUsed} chunks</Badge>
                <Badge variant="secondary" data-testid={`badge-images-used${idSuffix}`}>{suggest.imagesUsed} images</Badge>
              </>
            )}
            {improve && (
              <Badge variant="secondary" data-testid={`badge-improve-chunks-used${idSuffix}`}>{improve.chunksUsed} chunks</Badge>
            )}
          </div>
        </div>
        <div className="bg-gray-50 border border-gray-100 rounded-md p-4 text-sm whitespace-pre-wrap text-gray-900" data-testid={`text-output${idSuffix}`}>
          {outputText}
        </div>
        {suggest && acceptableDraft.length > 0 && onAccept && (
          <div className="flex justify-end">
            <Button size="sm" variant="outline" onClick={() => onAccept(acceptableDraft)} data-testid={`button-accept-into-thread${idSuffix}`}>
              <CornerDownRight className="h-3.5 w-3.5 mr-1.5" /> Accept into thread
            </Button>
          </div>
        )}
      </div>

      {/* Context breakdown (AI Draft Context Overhaul) — exactly what the
          draft targeted and used: the corrected selection made visible. */}
      {suggest?.context && (
        <ContextBreakdownCard context={suggest.context} visionSummaries={suggest.debug.visionSummaries} idSuffix={idSuffix} />
      )}

      {/* Pipeline debug */}
      <div className="border border-gray-200 rounded-lg p-5 space-y-4">
        <button
          className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 uppercase tracking-wide"
          onClick={onToggleDebug}
          data-testid={`button-toggle-debug${idSuffix}`}
        >
          {showDebug ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          Pipeline debug
        </button>

        {showDebug && (
          <div className="space-y-5">
            <DebugField label="Retrieval query" value={debug.queryText || "—"} testId={`debug-query${idSuffix}`} />

            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase mb-2">
                Retrieved chunks ({debug.retrievedChunks.length})
              </div>
              {debug.retrievedChunks.length === 0 ? (
                <div className="text-sm text-gray-400">No chunks retrieved.</div>
              ) : (
                <div className="space-y-2">
                  {debug.retrievedChunks.map((ch, i) => (
                    <div key={i} className="border border-gray-100 rounded-md p-3 bg-gray-50" data-testid={`debug-chunk${idSuffix}-${i}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-xs">{chunkSourceLabel(ch.sourceType)}</Badge>
                        {typeof ch.score === "number" && (
                          <span className="text-xs text-gray-400">sim {ch.score.toFixed(4)}</span>
                        )}
                        {ch.propertyId != null && (
                          <span className="text-xs text-gray-400">property #{ch.propertyId}</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-700 whitespace-pre-wrap">{ch.chunkText}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase mb-2">
                Pinned company voice ({debug.pinnedVoice.length})
              </div>
              {debug.pinnedVoice.length === 0 ? (
                <div className="text-sm text-gray-400">No pinned company voice chunks.</div>
              ) : (
                <div className="space-y-2">
                  {debug.pinnedVoice.map((ch, i) => (
                    <div key={i} className="border border-gray-100 rounded-md p-3 bg-gray-50" data-testid={`debug-pinned${idSuffix}-${i}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-xs">{chunkSourceLabel(ch.sourceType)}</Badge>
                        {typeof ch.score === "number" && (
                          <span className="text-xs text-gray-400">sim {ch.score.toFixed(4)}</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-700 whitespace-pre-wrap">{ch.chunkText}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <DebugField label="Context block" value={debug.contextBlock || "—"} testId={`debug-context${idSuffix}`} mono />
            <DebugField label="House voice" value={debug.houseVoice} testId={`debug-voice${idSuffix}`} mono />

            {"effectiveRunCount" in debug && (
              <div className="flex gap-4 text-xs text-gray-600 flex-wrap">
                <span data-testid={`debug-run-count${idSuffix}`}>Member run: {debug.effectiveRunCount} msg(s)</span>
                <span data-testid={`debug-last-host${idSuffix}`}>Last host included: {String(debug.lastHostIncluded)}</span>
                <span data-testid={`debug-images${idSuffix}`}>Images used: {debug.imagesUsed}</span>
                {debug.imageCaption && (
                  <span data-testid={`debug-caption${idSuffix}`}>Caption: {debug.imageCaption}</span>
                )}
              </div>
            )}

            <DebugField label="Full prompt" value={debug.prompt} testId={`debug-prompt${idSuffix}`} mono />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Reusable house-voice editor: live-vs-override selector + content. Rendered
// once for single mode, twice (Voice A / Voice B) in compare mode. ──
function VoiceEditor({
  label, mode, onModeChange, override, onOverrideChange, liveVoice, liveLoading, idSuffix = "",
}: {
  label?: string;
  mode: VoiceMode;
  onModeChange: (mode: VoiceMode) => void;
  override: string;
  onOverrideChange: (text: string) => void;
  liveVoice: string;
  liveLoading: boolean;
  idSuffix?: string;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        {label && <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</span>}
        <div className={label ? "w-48" : "w-64 ml-auto"}>
          <Select value={mode} onValueChange={(v) => onModeChange(v as VoiceMode)}>
            <SelectTrigger data-testid={`select-voice-mode${idSuffix}`} className="h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="live">Use live global voice</SelectItem>
              <SelectItem value="override">Use a scratch override</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {mode === "live" ? (
        <div className="bg-gray-50 border border-gray-100 rounded-md p-3 text-xs text-gray-600 whitespace-pre-wrap max-h-48 overflow-y-auto" data-testid={`text-live-voice${idSuffix}`}>
          {liveLoading ? "Loading current global voice…" : liveVoice}
        </div>
      ) : (
        <Textarea
          data-testid={`textarea-voice-override${idSuffix}`}
          placeholder="Type the scratch house voice to use for this simulation only. (Leaving this blank falls back to the live global voice.)"
          value={override}
          onChange={(e) => onOverrideChange(e.target.value)}
          rows={3}
        />
      )}
    </div>
  );
}

function ConversationRow({
  msg, idx, canRemove, onRoleChange, onTextChange, onResolvedChange, onUploadAttachment, onRemoveAttachment, onRemove,
}: {
  msg: Msg;
  idx: number;
  canRemove: boolean;
  onRoleChange: (role: Msg["role"]) => void;
  onTextChange: (text: string) => void;
  onResolvedChange: (resolved: boolean | undefined) => void;
  onUploadAttachment: (file: File) => Promise<void>;
  onRemoveAttachment: (i: number) => void;
  onRemove: () => void;
}) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        if (msg.attachments.length >= 8) break;
        if (!file.type.startsWith("image/")) {
          toast({ title: "Not an image", description: `${file.name} was skipped.`, variant: "destructive" });
          continue;
        }
        await onUploadAttachment(file);
      }
    } catch (err) {
      toast({ title: "Upload failed", description: parseErrorMessage(err, "Could not upload the image. Try again."), variant: "destructive" });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  return (
    <div className="flex gap-3 items-start" data-testid={`row-msg-${idx}`}>
      <div className="w-28 shrink-0 pt-1.5">
        <Select value={msg.role} onValueChange={(v) => onRoleChange(v as Msg["role"])}>
          <SelectTrigger data-testid={`select-role-${idx}`} className="h-8">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="member">Member</SelectItem>
            <SelectItem value="host">Host</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex-1 space-y-2">
        <Textarea
          data-testid={`textarea-msg-${idx}`}
          value={msg.text}
          placeholder={msg.role === "member" ? "What the member says…" : "Host reply…"}
          onChange={(e) => onTextChange(e.target.value)}
          rows={2}
        />
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            data-testid={`input-attach-file-${idx}`}
            onChange={(e) => handleFiles(e.target.files)}
          />
          <Button
            size="sm" variant="outline" className="h-8"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || msg.attachments.length >= 8}
            data-testid={`button-attach-add-${idx}`}
          >
            <Paperclip className="h-3.5 w-3.5 mr-1.5" />
            {uploading ? "Uploading…" : "Attach image"}
          </Button>
          {msg.role === "member" && (
            // Resolution marker (Phase 4): cycles unmarked → open → resolved.
            // Marking ANY message switches selection to production
            // unresolved-first semantics (unmarked messages count as open).
            <Button
              size="sm"
              variant={msg.resolved === undefined ? "ghost" : "outline"}
              className={`h-8 text-xs ${msg.resolved === true ? "text-green-700 border-green-300" : msg.resolved === false ? "text-amber-700 border-amber-300" : "text-gray-400"}`}
              onClick={() =>
                onResolvedChange(msg.resolved === undefined ? false : msg.resolved === false ? true : undefined)
              }
              title="Cycle resolution marker: unmarked → open → resolved"
              data-testid={`button-resolved-toggle-${idx}`}
            >
              {msg.resolved === true ? "Resolved" : msg.resolved === false ? "Open" : "Mark resolution"}
            </Button>
          )}
        </div>
        {msg.attachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {msg.attachments.map((att, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 rounded px-2 py-0.5 text-xs max-w-[260px]"
                data-testid={`chip-attach-${idx}-${i}`}
              >
                <span className="truncate" title={att.url}>{att.name}</span>
                <button
                  onClick={() => onRemoveAttachment(i)}
                  className="text-gray-400 hover:text-red-600 shrink-0"
                  data-testid={`button-attach-remove-${idx}-${i}`}
                  aria-label="Remove attachment"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
      <Button
        size="icon" variant="ghost"
        className="text-gray-400 hover:text-red-600 mt-1"
        onClick={onRemove}
        disabled={!canRemove}
        data-testid={`button-remove-msg-${idx}`}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

// ── Context breakdown card: which messages the draft targeted (unresolved
// ids), which priors the relevance gate kept and WHY, image understanding
// stats, and fallback/continuity flags. This is the primary way the corrected
// selection is verified at a glance. ──
function ContextBreakdownCard({
  context, visionSummaries, idSuffix = "",
}: {
  context: ContextBreakdown;
  visionSummaries?: SuggestDebug["visionSummaries"];
  idSuffix?: string;
}) {
  const analyzed = (visionSummaries ?? []).filter((v) => v.summary !== null);
  return (
    <div className="border border-gray-200 rounded-lg p-5 space-y-4" data-testid={`card-context-breakdown${idSuffix}`}>
      <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Context breakdown</h2>

      <div className="flex flex-wrap gap-2">
        <Badge variant="secondary" data-testid={`breakdown-unresolved${idSuffix}`}>
          {context.unresolvedMessageIds.length} open message{context.unresolvedMessageIds.length === 1 ? "" : "s"} targeted
        </Badge>
        {context.unresolvedTruncated && (
          <Badge variant="outline" className="text-amber-700 border-amber-300" data-testid={`breakdown-truncated${idSuffix}`}>
            verbatim cap hit — oldest summarized
          </Badge>
        )}
        {context.fallbackUsed && (
          <Badge variant="outline" className="text-blue-700 border-blue-300" data-testid={`breakdown-fallback${idSuffix}`}>
            follow-up draft (nothing open — last-24h fallback)
          </Badge>
        )}
        <Badge variant="secondary" data-testid={`breakdown-continuity${idSuffix}`}>{context.continuityCount} host continuity</Badge>
        <Badge variant="secondary" data-testid={`breakdown-images${idSuffix}`}>
          {context.imagesAnalyzed} image{context.imagesAnalyzed === 1 ? "" : "s"} understood
          {context.imagesAnalyzed > 0 ? ` (${context.imagesCached} cached)` : ""}
        </Badge>
        <Badge variant="secondary" data-testid={`breakdown-chunks${idSuffix}`}>{context.knowledgeChunkIds.length} KB chunks</Badge>
      </div>

      {context.unresolvedMessageIds.length > 0 && (
        <div className="text-xs text-gray-600" data-testid={`breakdown-unresolved-ids${idSuffix}`}>
          Reply-target message ids: {context.unresolvedMessageIds.join(", ")}
        </div>
      )}

      <div>
        <div className="text-xs font-semibold text-gray-500 uppercase mb-2">
          Lookback priors — {context.priorsKept.length} kept of {context.priorsConsidered} considered
        </div>
        {context.priorsKept.length === 0 ? (
          <div className="text-sm text-gray-400" data-testid={`breakdown-priors-none${idSuffix}`}>
            {context.priorsConsidered === 0
              ? "No earlier messages in the lookback window."
              : "The relevance gate judged none of the earlier messages necessary for this reply."}
          </div>
        ) : (
          <div className="space-y-1.5">
            {context.priorsKept.map((p, i) => (
              <div key={i} className="text-xs text-gray-700 bg-gray-50 border border-gray-100 rounded-md px-3 py-2" data-testid={`breakdown-prior${idSuffix}-${i}`}>
                {p.messageId != null && <span className="text-gray-400 mr-1.5">#{p.messageId}</span>}
                <span className="italic">relevant because: {p.reason}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {analyzed.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Image understanding</div>
          <div className="space-y-1.5">
            {analyzed.map((v, i) => (
              <div key={i} className="text-xs text-gray-700 bg-gray-50 border border-gray-100 rounded-md px-3 py-2" data-testid={`breakdown-vision${idSuffix}-${i}`}>
                <span className="text-gray-400 mr-1.5">{v.cached ? "cached" : "fresh"}</span>
                {v.summary!.description}
                {v.summary!.apparentIssue ? ` — issue: ${v.summary!.apparentIssue}` : ""}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DebugField({ label, value, testId, mono }: { label: string; value: string; testId: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-xs font-semibold text-gray-500 uppercase mb-1">{label}</div>
      <pre
        className={`text-xs whitespace-pre-wrap break-words bg-gray-50 border border-gray-100 rounded-md p-3 text-gray-700 max-h-72 overflow-y-auto ${mono ? "font-mono" : ""}`}
        data-testid={testId}
      >
        {value}
      </pre>
    </div>
  );
}
